import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star, MapPin, Navigation } from "lucide-react";
import { WhatsAppIcon } from "./icons";
import { EASE, MaskedLine } from "./Reveal";
import { WHATSAPP_URL, MAPS_URL, scrollToId } from "./config";

const HERO_IMG =
  "https://images.unsplash.com/photo-1586985289688-ca3cf47d3e6e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxjYWtlJTIwZGVjb3JhdGluZyUyMHRvb2xzJTIwcGlwaW5nJTIwbm96emxlcyUyMGZvbmRhbnQlMjBzcHJpbmtsZXMlMjBjb2xvcmZ1bCUyMGJha2VyeSUyMHN1cHBseXxlbnwwfHx8fDE3OTI5NjExNjd8MA&ixlib=rb-4.1.0&q=85";

const Stars = ({ className = "h-4 w-4" }) => (
  <span className="flex gap-0.5">
    {[...Array(5)].map((_, i) => (
      <Star key={i} className={`${className} fill-gold text-gold`} />
    ))}
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const blobY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      ref={ref}
      className="relative overflow-hidden pb-14 pt-28 sm:pt-32 lg:pb-14 lg:pt-36"
    >
      {/* Backdrop blobs */}
      <motion.div
        style={{ y: blobY }}
        aria-hidden="true"
        className="absolute -top-40 right-[-10%] h-[540px] w-[540px]"
      >
        <div
          className="h-full w-full rounded-full"
          style={{
            background:
              "radial-gradient(closest-side, rgba(253,232,237,0.95), rgba(253,232,237,0))",
          }}
        />
      </motion.div>
      <div
        aria-hidden="true"
        className="dots-pattern absolute left-[-40px] top-1/3 h-56 w-56 opacity-[0.18] [mask-image:radial-gradient(black,transparent_70%)]"
      />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-4 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:px-12">
        {/* Copy */}
        <div className="lg:col-span-7">
          <motion.span
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05, ease: EASE }}
            className="inline-flex items-center gap-2 rounded-full border border-linebr bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-cocoa shadow-sm"
          >
            <MapPin className="h-3.5 w-3.5 text-rose" />
            Decoración de pasteles · Guayaquil
          </motion.span>

          <h1 className="mt-5 font-display text-[clamp(2.5rem,5.6vw,4.6rem)] font-semibold leading-[1.04] tracking-tight text-choco">
            <MaskedLine delay={0.15}>Todo para tus</MaskedLine>
            <MaskedLine delay={0.27}>creaciones de</MaskedLine>
            <MaskedLine delay={0.39}>
              <em className="relative inline-block font-light italic text-rose">
                repostería
                <svg
                  className="ink-underline absolute -bottom-2 left-0 w-full"
                  viewBox="0 0 220 14"
                  fill="none"
                  preserveAspectRatio="none"
                  aria-hidden="true"
                >
                  <motion.path
                    d="M4 10C50 3 120 2 216 8"
                    stroke="#E8A238"
                    strokeWidth="5"
                    strokeLinecap="round"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.7, delay: 1.15, ease: EASE }}
                  />
                </svg>
              </em>
            </MaskedLine>
            <MaskedLine delay={0.51}>en un solo lugar.</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75, ease: EASE }}
            className="mt-6 max-w-xl text-base leading-relaxed text-cocoa sm:text-lg"
          >
            Encuentra la mayor variedad de insumos, herramientas y accesorios
            de decoración para pasteles en Guayaquil. Calidad profesional para
            que tus postres luzcan inolvidables.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9, ease: EASE }}
            className="mt-7 flex flex-col gap-3.5 sm:flex-row sm:items-center"
          >
            <a
              data-testid="hero-whatsapp-button"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center justify-center gap-3 rounded-full bg-wagreen px-8 py-4 text-base font-bold text-white shadow-[0_18px_40px_-14px_rgba(37,211,102,0.65)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_24px_48px_-14px_rgba(37,211,102,0.75)]"
            >
              <WhatsAppIcon className="h-5 w-5 transition duration-300 group-hover:rotate-12" />
              Pedir por WhatsApp Ahora
            </a>
            <button
              data-testid="hero-maps-button"
              onClick={() => scrollToId("ubicacion")}
              className="inline-flex items-center justify-center gap-2.5 rounded-full border-2 border-choco/15 bg-white/70 px-8 py-4 text-base font-bold text-choco backdrop-blur transition duration-300 hover:-translate-y-1 hover:border-rose hover:text-rose"
            >
              <Navigation className="h-5 w-5" />
              Ver Cómo Llegar a Plaza Quil
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.05, ease: EASE }}
            className="mt-7 flex flex-wrap items-center gap-x-4 gap-y-2"
          >
            <Stars />
            <span className="text-sm font-bold text-choco">
              4.9 / 5 estrellas en Google
            </span>
            <span className="hidden h-1 w-1 rounded-full bg-sand sm:block" />
            <span className="text-sm text-cocoa">
              Reposteros y emprendedores nos recomiendan
            </span>
          </motion.div>
        </div>

        {/* Visual */}
        <div className="relative lg:col-span-5">
          <motion.div
            style={{ y: imgY }}
            initial={{ opacity: 0, scale: 0.94, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.45, ease: EASE }}
            className="relative mx-auto max-w-md lg:max-w-none"
          >
            <div
              aria-hidden="true"
              className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-gold/25 blur-2xl"
            />
            <div className="relative overflow-hidden rounded-b-[2rem] rounded-t-[12rem] border-[6px] border-white shadow-[0_40px_90px_-30px_rgba(44,32,28,0.45)]">
              <motion.img
                src={HERO_IMG}
                alt="Decoración profesional de pastel con manga pastelera y crema"
                initial={{ scale: 1.18 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.8, delay: 0.45, ease: EASE }}
                className="h-[360px] w-full object-cover sm:h-[440px]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-choco/25 via-transparent to-transparent" />
            </div>

            {/* Floating Google card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 16, delay: 1.3 }}
              className="animate-float-slow absolute -left-4 top-10 rounded-2xl border border-linebr bg-white/95 px-5 py-4 shadow-[0_24px_50px_-20px_rgba(44,32,28,0.4)] backdrop-blur sm:-left-10"
              data-testid="hero-google-card"
            >
              <div className="flex items-center gap-3">
                <span className="font-display text-3xl font-bold text-choco">4.9</span>
                <span>
                  <Stars className="h-3.5 w-3.5" />
                  <span className="mt-0.5 block text-[11px] font-bold text-cocoa">
                    Opiniones en Google
                  </span>
                </span>
              </div>
            </motion.div>

            {/* Hand note */}
            <motion.span
              initial={{ opacity: 0, rotate: 0 }}
              animate={{ opacity: 1, rotate: -6 }}
              transition={{ duration: 0.6, delay: 1.5, ease: EASE }}
              className="absolute -bottom-6 right-2 rounded-2xl bg-rose px-5 py-2.5 font-hand text-2xl font-semibold text-cream shadow-lg"
            >
              ¡Nos vemos en Plaza Quil!
            </motion.span>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
